/**
 * Numbers that describe work: how long it took, how much it cost, how often it repeats.
 *
 * All three are formatting concerns rather than translation concerns wherever `Intl` can do
 * the job — a compact token count reads correctly in Hebrew with no catalogue entry. Only the
 * duration units need words, and they are short by design: a run list is scanned, not read.
 */
type Translate = (key: string, options?: Record<string, unknown>) => string;

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "42s", "7m", "3h 20m", "2d 4h" — one unit of precision more than the eye needs, never two. */
export function formatDuration(ms: number | null | undefined, t: Translate): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) {
    return "—";
  }
  if (ms < MINUTE) {
    return t("{{value}}s", { value: Math.max(1, Math.round(ms / SECOND)) });
  }
  if (ms < HOUR) {
    return t("{{value}}m", { value: Math.round(ms / MINUTE) });
  }
  if (ms < DAY) {
    const hours = Math.floor(ms / HOUR);
    const minutes = Math.round((ms % HOUR) / MINUTE);
    return minutes === 0
      ? t("{{value}}h", { value: hours })
      : `${t("{{value}}h", { value: hours })} ${t("{{value}}m", { value: minutes })}`;
  }
  const days = Math.floor(ms / DAY);
  const hours = Math.round((ms % DAY) / HOUR);
  return hours === 0
    ? t("{{value}}d", { value: days })
    : `${t("{{value}}d", { value: days })} ${t("{{value}}h", { value: hours })}`;
}

/** 138,880,676 tokens is unreadable; "139M" is the number somebody can act on. */
export function formatCompact(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }
  return new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 }).format(
    value,
  );
}

export function formatPercent(ratio: number | null | undefined, locale: string): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) {
    return "—";
  }
  return new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 }).format(
    ratio,
  );
}

/**
 * The platform stores a recurrence as minutes ("1440m"). Rendering that verbatim asks the
 * reader to divide; rendering it as a duration answers the question they actually have.
 */
export function formatRecurrence(recurrence: string | null, t: Translate): string {
  if (!recurrence) {
    return "—";
  }
  const minutes = Number.parseInt(recurrence.replace(/[^0-9]/g, ""), 10);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return recurrence;
  }
  return t("every {{interval}}", { interval: formatDuration(minutes * MINUTE, t) });
}

/**
 * A token count that can run past a hundred million (one run measured 96.7% cache-read at
 * 91.5M tokens). "≥1e6 → 1.23M", "≥1e3 → 1.2k", else the integer, `null → "—"` — ported
 * verbatim from kms's `compactTokens` (W2 §A.1).
 */
export function compactTokens(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }
  const abs = Math.abs(value);
  if (abs >= 1e6) {
    return `${(value / 1e6).toFixed(2)}M`;
  }
  if (abs >= 1e3) {
    return `${(value / 1e3).toFixed(1)}k`;
  }
  return String(Math.round(value));
}

/** `(x*100).toFixed(1) + "%"`, `null → "—"` — kms's `formatShare`, ported verbatim. */
export function formatShare(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) {
    return "—";
  }
  return `${(ratio * 100).toFixed(1)}%`;
}

/** `<1000 → "412ms"`, else `"1.4s"` — kms's `formatMs`, ported verbatim. */
export function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) {
    return "—";
  }
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  return `${(ms / 1000).toFixed(1)}s`;
}

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

/** `bytesToHumanReadable` (Outline shared/utils/files, reproduced verbatim — E.2). */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) {
    return "—";
  }
  if (bytes === 0) {
    return "0 B";
  }
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${exponent === 0 ? value : value.toFixed(1)} ${BYTE_UNITS[exponent]}`;
}
